import { useState, useEffect } from 'react';
import { TextField, Button, Box, Autocomplete, CircularProgress } from '@mui/material';
import { goApi } from '../../../services';
import { useNotify } from 'react-admin';
import { apiRoutes } from '../../../services/apiRoutes';
import { useParams } from 'react-router';

const BatchTopicForm = ({ record, onCancel, templateId, onSuccess }: any) => {
    const [formData, setFormData] = useState({
        title: record?.title || '',
        displayOrder: record?.displayOrder || '',
        subjectId: record?.subjectId || '',
    });
    const [subjects, setSubjects] = useState<any[]>([]);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const notify = useNotify();
    const params = useParams();
    const BatchId = params?.id;
    useEffect(() => {
        const fetchSubjects = async () => {
            if (!templateId) return;
            setLoadingSubjects(true);
            try {
                const response = await goApi.get(
                    `${apiRoutes.BATCH_TEMPLATE_SUBJECTS}?batchTemplateId=${templateId}&limit=50`,
                );
                setSubjects(response.data?.items || []);
            } catch (err) {
                notify('Failed to fetch subjects', { type: 'error' });
            } finally {
                setLoadingSubjects(false);
            }
        };
        fetchSubjects();
    }, [templateId, record]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (record) {
                await goApi.put(`${apiRoutes.BATCH_TOPICS}/${record?.id}`, {
                    title: formData.title,
                    displayOrder: Number(formData.displayOrder),
                    subjectId: formData.subjectId,
                    batchId: BatchId,
                });
                notify('Batch topic updated', { type: 'success' });
            } else {
                await goApi.post(`${apiRoutes.BATCH_TOPICS}`, {
                    batchId: BatchId,
                    title: formData.title,
                    displayOrder: Number(formData.displayOrder),
                    subjectId: formData.subjectId,
                });
                notify('Batch topic created', { type: 'success' });
            }
            onSuccess?.();
        } catch (err) {
            notify(`${record ? 'Update' : 'Create'} failed`, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}
        >
            <TextField
                label="Title"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                required
            />
            <TextField
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={e => setFormData({ ...formData, displayOrder: e.target.value })}
                fullWidth
                required
            />
            <Autocomplete
                options={subjects}
                getOptionLabel={option => option.name || option.title || ''}
                value={subjects?.find(s => s.subjectId === formData.subjectId) || null}
                onChange={(_, newValue) => {
                    setFormData({ ...formData, subjectId: newValue?.subjectId || '' });
                }}
                loading={loadingSubjects}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Subject"
                        required
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingSubjects ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={onCancel} disabled={submitting}>
                    Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save'}
                </Button>
            </Box>
        </Box>
    );
};

export default BatchTopicForm;
